import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, Gamepad2, Car } from "lucide-react";

const things = [
  "Brain fog — thoughts can feel slow.",
  "Wobbles, shakes or movements they didn't choose.",
  "Sudden tiredness, even after small things.",
  "Some days bumpy, some days smooth.",
];

const helps = [
  "Speak softly. A kind voice helps.",
  "Don't rush them — let the remote find the channel.",
  "Treat them normally. They're still the same person.",
  "Believe them — if they say it feels weird, it does.",
];

const truths = [
  "FND is real.",
  "It is not their fault.",
  "It is not catching — you can't get it from a hug.",
  "Their brain is clever, just a little crossed-up.",
];

const myths = [
  "They are not making it up.",
  "They are not being lazy.",
  "They can't just 'try harder' to stop it.",
  "It doesn't mean they're broken.",
];

function FillLines({ count = 3, testid }) {
  return (
    <div className="space-y-3.5 mt-3" data-testid={testid}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="h-px border-b border-dashed border-charcoal/35 print:border-black/40"
        />
      ))}
    </div>
  );
}

export default function ForKidsPrint() {
  useEffect(() => {
    document.title = "Quiet Signals — A Little Booklet About FND";
    return () => {
      document.title = "Quiet Signals — Understanding FND";
    };
  }, []);

  const handlePrint = () => window.print();

  return (
    <div data-testid="page-for-kids-print" className="bg-bone-100/60 print:bg-white min-h-screen">
      {/* Top action bar — hidden in print */}
      <div className="print:hidden border-b border-bone-200 bg-bone sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <Link
            to="/for-kids"
            data-testid="print-back-link"
            className="inline-flex items-center gap-2 text-sm text-charcoal hover:text-terracotta transition"
          >
            <ArrowLeft size={16} strokeWidth={1.5} />
            Back to For Kids
          </Link>
          <div className="flex items-center gap-3">
            <p className="hidden sm:block text-xs text-charcoal-light">
              A4 · 3 pages · ready to print
            </p>
            <button
              type="button"
              onClick={handlePrint}
              data-testid="print-button"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-terracotta text-bone text-sm font-medium hover:bg-terracotta-hover transition"
            >
              <Printer size={16} strokeWidth={1.5} />
              Print booklet
            </button>
          </div>
        </div>
      </div>

      {/* Booklet pages */}
      <div className="max-w-5xl mx-auto px-6 py-10 print:p-0 print:max-w-none">
        {/* PAGE 1 — Cover + Analogy */}
        <article
          data-testid="print-page-1"
          className="bg-white rounded-3xl print:rounded-none shadow-sm print:shadow-none border border-bone-200 print:border-0 p-10 sm:p-14 print:p-0 mb-8 print:mb-0 break-after-page print:min-h-[27cm]"
        >
          <header className="border-b-2 border-terracotta/30 pb-6 mb-10">
            <p className="text-[0.65rem] uppercase tracking-[0.32em] text-terracotta font-medium">
              Quiet Signals · A little booklet
            </p>
            <h1 className="font-serif text-charcoal text-4xl sm:text-5xl leading-[1.05] mt-3 tracking-tight">
              About my
              <span className="italic text-terracotta"> tricky remote </span>
              and
              <span className="italic text-terracotta"> kind little car</span>.
            </h1>
            <p className="mt-4 text-base text-charcoal-light leading-relaxed max-w-2xl">
              A calm way to explain Functional Neurological Disorder (FND) to a child.
              Read it together. Take your time.
            </p>
          </header>

          {/* The analogy */}
          <section className="mb-10">
            <p className="text-xs uppercase tracking-[0.28em] text-moss mb-3">The tiny story</p>
            <blockquote className="font-serif text-2xl sm:text-3xl text-charcoal leading-snug italic border-l-2 border-terracotta pl-6">
              “Your brain is like the remote, and your body is like the car.
              Sometimes the signal gets fuzzy, so the car might turn or stop
              even when you didn’t press the button.”
            </blockquote>
          </section>

          {/* Visual */}
          <section className="flex items-center justify-center gap-4 sm:gap-8 my-12">
            <div className="flex flex-col items-center">
              <div className="w-24 h-32 rounded-2xl bg-terracotta text-bone flex flex-col items-center justify-center gap-1.5 print:bg-terracotta print:text-white">
                <Gamepad2 size={26} strokeWidth={1.5} />
                <span className="text-[0.55rem] uppercase tracking-[0.2em]">Brain</span>
              </div>
              <span className="mt-2 text-xs text-charcoal-light">Remote</span>
            </div>

            <svg width="90" height="40" viewBox="0 0 90 40" className="text-charcoal-light">
              <path
                d="M2 20 L14 6 L26 34 L38 6 L50 34 L62 6 L74 34 L88 20"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                strokeDasharray="3 4"
              />
            </svg>

            <div className="flex flex-col items-center">
              <div className="w-28 h-24 rounded-2xl bg-moss text-bone flex flex-col items-center justify-center gap-1.5 print:bg-moss print:text-white">
                <Car size={26} strokeWidth={1.5} />
                <span className="text-[0.55rem] uppercase tracking-[0.2em]">Body</span>
              </div>
              <span className="mt-2 text-xs text-charcoal-light">Car</span>
            </div>
          </section>

          <p className="text-base text-charcoal-light leading-relaxed text-center max-w-xl mx-auto">
            That’s all FND really is — a fuzzy signal between a brain that’s
            working hard and a body that’s listening.
          </p>

          <footer className="mt-auto pt-10 text-[0.65rem] uppercase tracking-[0.28em] text-charcoal-light/70 text-center">
            Page 1 of 3 · Quiet Signals
          </footer>
        </article>

        {/* PAGE 2 — Things, Helps, Truths */}
        <article
          data-testid="print-page-2"
          className="bg-white rounded-3xl print:rounded-none shadow-sm print:shadow-none border border-bone-200 print:border-0 p-10 sm:p-14 print:p-0 mb-8 print:mb-0 break-after-page print:min-h-[27cm]"
        >
          <header className="border-b border-bone-200 pb-4 mb-8">
            <p className="text-[0.65rem] uppercase tracking-[0.32em] text-terracotta font-medium">
              What it can look like — and how to help
            </p>
            <h2 className="font-serif text-charcoal text-3xl sm:text-4xl mt-2 tracking-tight">
              Reading together.
            </h2>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-10">
            <section>
              <h3 className="font-serif text-xl text-charcoal mb-3 tracking-tight">
                Things that <em className="text-terracotta">might happen</em>
              </h3>
              <ul className="space-y-2.5">
                {things.map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-charcoal leading-relaxed">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h3 className="font-serif text-xl text-charcoal mb-3 tracking-tight">
                How to <em className="text-moss">be a good friend</em>
              </h3>
              <ul className="space-y-2.5">
                {helps.map((h, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-charcoal leading-relaxed">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-moss shrink-0" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <section className="rounded-2xl border-2 border-moss/40 p-5 print:border-moss/60">
              <h3 className="font-serif text-lg text-charcoal mb-3 tracking-tight">
                ✓ This is true
              </h3>
              <ul className="space-y-2 text-sm text-charcoal">
                {truths.map((t, i) => (
                  <li key={i} className="flex items-start gap-2 leading-relaxed">
                    <span className="text-moss font-bold shrink-0">✓</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="rounded-2xl border-2 border-terracotta/40 p-5 print:border-terracotta/60">
              <h3 className="font-serif text-lg text-charcoal mb-3 tracking-tight">
                ✗ These aren’t true
              </h3>
              <ul className="space-y-2 text-sm text-charcoal">
                {myths.map((m, i) => (
                  <li key={i} className="flex items-start gap-2 leading-relaxed">
                    <span className="text-terracotta font-bold shrink-0">✗</span>
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <footer className="mt-12 pt-6 text-[0.65rem] uppercase tracking-[0.28em] text-charcoal-light/70 text-center">
            Page 2 of 3 · Quiet Signals
          </footer>
        </article>

        {/* PAGE 3 — Fillable about-me sheet */}
        <article
          data-testid="print-page-3"
          className="bg-white rounded-3xl print:rounded-none shadow-sm print:shadow-none border border-bone-200 print:border-0 p-10 sm:p-14 print:p-0 print:min-h-[27cm]"
        >
          <header className="border-b border-bone-200 pb-4 mb-8">
            <p className="text-[0.65rem] uppercase tracking-[0.32em] text-terracotta font-medium">
              All about me
            </p>
            <h2 className="font-serif text-charcoal text-3xl sm:text-4xl mt-2 tracking-tight">
              My signal, in <em className="text-terracotta">my own words</em>.
            </h2>
            <p className="mt-3 text-sm text-charcoal-light leading-relaxed max-w-xl">
              Fill this in together. Take it to school, to grandma’s, to a new doctor —
              anywhere it might help someone understand you better.
            </p>
          </header>

          {/* Quick facts row */}
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 mb-8">
            <div data-testid="field-name">
              <label className="text-[0.65rem] uppercase tracking-[0.22em] text-charcoal-light">My name</label>
              <div className="h-px border-b border-dashed border-charcoal/40 mt-7" />
            </div>
            <div data-testid="field-age">
              <label className="text-[0.65rem] uppercase tracking-[0.22em] text-charcoal-light">My age</label>
              <div className="h-px border-b border-dashed border-charcoal/40 mt-7" />
            </div>
            <div data-testid="field-diagnosed">
              <label className="text-[0.65rem] uppercase tracking-[0.22em] text-charcoal-light">When I was diagnosed</label>
              <div className="h-px border-b border-dashed border-charcoal/40 mt-7" />
            </div>
            <div data-testid="field-safe-person">
              <label className="text-[0.65rem] uppercase tracking-[0.22em] text-charcoal-light">My safe person & their number</label>
              <div className="h-px border-b border-dashed border-charcoal/40 mt-7" />
            </div>
          </section>

          {/* My symptoms */}
          <section className="mb-8" data-testid="field-symptoms">
            <h3 className="font-serif text-xl text-charcoal mb-1 tracking-tight">
              What my <em className="text-terracotta">FND can look like</em>
            </h3>
            <p className="text-xs text-charcoal-light italic mb-2">
              (E.g. shakes in my hands, blurred vision, falling, brain fog…)
            </p>
            <FillLines count={4} testid="lines-symptoms" />
          </section>

          {/* What helps me */}
          <section className="mb-8" data-testid="field-helps">
            <h3 className="font-serif text-xl text-charcoal mb-1 tracking-tight">
              What <em className="text-moss">really helps me</em>
            </h3>
            <p className="text-xs text-charcoal-light italic mb-2">
              (E.g. somewhere quiet, water, my favourite blanket, a hug…)
            </p>
            <FillLines count={4} testid="lines-helps" />
          </section>

          {/* What doesn't help */}
          <section className="mb-8" data-testid="field-not-helps">
            <h3 className="font-serif text-xl text-charcoal mb-1 tracking-tight">
              Things that <em className="text-terracotta">don’t help</em>
            </h3>
            <p className="text-xs text-charcoal-light italic mb-2">
              (E.g. loud noises, being rushed, bright lights, “just try harder”…)
            </p>
            <FillLines count={3} testid="lines-not-helps" />
          </section>

          {/* If you see me having a wobble */}
          <section className="mb-8 rounded-2xl border-2 border-terracotta/40 p-5 print:border-terracotta/60" data-testid="field-emergency">
            <h3 className="font-serif text-xl text-charcoal mb-1 tracking-tight">
              If you see me having a wobble, please…
            </h3>
            <p className="text-xs text-charcoal-light italic mb-2">
              (Stay calm, sit me down, call my safe person, give me time…)
            </p>
            <FillLines count={3} testid="lines-emergency" />
          </section>

          <footer className="pt-6 text-[0.65rem] uppercase tracking-[0.28em] text-charcoal-light/70 text-center">
            Page 3 of 3 · Quiet Signals · quietsignals.org
          </footer>
        </article>
      </div>
    </div>
  );
}
